import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { defaultExpressCheckoutComponents } from './express-checkout-components';
import { resolveDeviceProviderSubId } from './base-express-checkout';
import {
  ChangeDetectorRef,
  ComponentRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AuthService,
  config,
  EXPRESS_CHECKOUT_PROVIDERS,
  ExpressCheckoutProvider,
  ExpressCheckoutProviderOptions,
  ExpressCheckoutResult,
  GiftCodeModel,
  LANGUAGE,
  LoggingService,
  MarketplaceExpressCheckoutContext,
  MemberQuery,
  ProviderName,
} from '@fgb/core';
import { firstValueFrom, Subscription } from 'rxjs';

/**
 * Container component for express checkout. It does not implement any single wallet itself —
 * instead it renders every express checkout provider registered via the
 * EXPRESS_CHECKOUT_PROVIDERS token (e.g. Paze) and forwards a shared context down to each, while
 * re-emitting their results back up to the host page.
 *
 * @example
 * ```html
 * <fgbcl-express-checkout
 *   [context]="expressContext"
 *   [totalAmount]="totalBasketPrice"
 *   [giftCodes]="appliedGiftCodes"
 *   (result)="onExpressResult($event)"
 *   (incomplete)="onExpressIncomplete($event)">
 * </fgbcl-express-checkout>
 * ```
 */
@Component({
  selector: 'fgbcl-express-checkout',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './express-checkout.component.html',
  styleUrl: './express-checkout.component.scss',
  providers: [
    {
      provide: EXPRESS_CHECKOUT_PROVIDERS,
      useValue: defaultExpressCheckoutComponents,
    },
  ],
})
export class ExpressCheckoutComponent implements OnInit, OnChanges, OnDestroy {
  @Input() providers: ProviderName[] = [ProviderName.FreedomPay, ProviderName.Paze, ProviderName.PayPal];
  /** Shared context (order id, amount, currency, customer identifier, urls) passed to every provider. */
  @Input() context: MarketplaceExpressCheckoutContext;

  @Input() totalAmount: number = 0;

  /**
   * Gift codes applied to the basket. Kept as its own input rather than read off `context` so a code
   * applied or removed after the providers have rendered is pushed into them, the same way
   * `totalAmount` is — the host recalculates both together.
   */
  @Input() giftCodes: GiftCodeModel[] = [];

  /** Re-emitted when any provider finishes (success or failure). */
  @Output() result = new EventEmitter<ExpressCheckoutResult>();

  /** Re-emitted when any provider reports the customer is ineligible / cancelled. */
  @Output() incomplete = new EventEmitter<ExpressCheckoutResult>();

  @ViewChild('providerHost', { read: ViewContainerRef, static: true }) providerHost: ViewContainerRef;

  private logService = inject(LoggingService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  private componentRefs: ComponentRef<ExpressCheckoutProvider>[] = [];
  private subscriptions = new Subscription();
  private providerComponents = inject(EXPRESS_CHECKOUT_PROVIDERS);
  private enabledSub = new Subscription();
  private memberQuery = inject(MemberQuery);
  private languages = inject(LANGUAGE);
  private authService = inject(AuthService);

  /**
   * Whether the providers are still initialising. Each reveals itself with its own `ready` flag as
   * its SDK settles, so without a container-level gate the buttons appear one at a time. The row
   * stays hidden until they have all finished, so it arrives as a single unit.
   */
  initialising = true;

  /** Caps the wait, so one provider that never settles cannot hold the others back forever. */
  private static readonly READY_TIMEOUT_MS = 5000;
  private static readonly READY_POLL_MS = 50;

  /** Whether at least one enabled provider is registered and rendered. */
  get hasProviders(): boolean {
    return this.providerEntries.length > 0;
  }

  /**
   * The providers to render: those the app enabled (EXPRESS_CHECKOUT_ENABLED), in that order, that
   * are also registered with a component (EXPRESS_CHECKOUT_PROVIDERS). Enabled-but-unregistered
   * providers are skipped so a config typo can't crash the container. FreedomPay (Google/Apple Pay
   * via HPC) is mobile-only, so it's excluded entirely on desktop — never instantiated, not just hidden.
   */
  private get providerEntries(): [ProviderName, ExpressCheckoutProviderOptions][] {
    const isMobileDevice = !!resolveDeviceProviderSubId();

    return this.providers
      .filter((providerId) => providerId !== ProviderName.FreedomPay || isMobileDevice)
      .map((providerId): [ProviderName, ExpressCheckoutProviderOptions | undefined] => [
        providerId,
        this.providerComponents[providerId],
      ])
      .filter((entry): entry is [ProviderName, ExpressCheckoutProviderOptions] => !!entry[1]);
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    this.clearProviders();

    if (!this.providerHost) {
      this.logService.error('ExpressCheckout: provider host not ready');
      return;
    }
    this.initializeProviders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The host usually computes the context before the order exists, so the first value carries an
    // empty orderId. A provider initialised against that has nothing to pay for and never becomes
    // ready, and nothing here used to react to the real id arriving - so rebuild them when the
    // order id changes, rather than pushing the new context into SDKs already bound to the old one.
    const contextChange = changes['context'];
    if (contextChange && !contextChange.firstChange) {
      const previousOrderId = contextChange.previousValue?.orderId ?? '';
      const currentOrderId = contextChange.currentValue?.orderId ?? '';
      if (previousOrderId !== currentOrderId) {
        this.initialising = true;
        this.clearProviders();
        void this.initializeProviders();
        return;
      }
    }

    if (changes['totalAmount'] && !changes['totalAmount'].firstChange) {
      this.updateTotalAmount(this.totalAmount);
    }
    if (changes['giftCodes'] && !changes['giftCodes'].firstChange) {
      this.updateGiftCodes(this.giftCodes);
    }
  }

  ngOnDestroy(): void {
    this.enabledSub.unsubscribe();
    this.clearProviders();
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Push an updated amount into every already-rendered provider's context, without a full re-init. */
  private updateTotalAmount(amount: number): void {
    this.componentRefs.forEach((componentRef) => {
      const currentContext = componentRef.instance.context;
      if (!currentContext) {
        return;
      }
      componentRef.setInput('context', { ...currentContext, amount });
    });
  }

  /** Push updated gift codes into every already-rendered provider's context, without a full re-init. */
  private updateGiftCodes(giftCodes: GiftCodeModel[]): void {
    this.componentRefs.forEach((componentRef) => {
      const currentContext = componentRef.instance.context;
      if (!currentContext) {
        return;
      }
      componentRef.setInput('context', { ...currentContext, giftCodes: giftCodes ?? [] });
    });
  }

  async initializeProviders(): Promise<void> {
    // Fetch every provider's init in parallel; allSettled so one failing does not reject the rest.
    const entries = this.providerEntries;

    if (!this.context) {
      this.revealProviders();
      return;
    }

    if (this.context.mode !== 'registration' && !this.context.orderId) {
      return;
    }

    const isAuthenticated = this.authService.isAuthenticated();

    let emailAddress: string = '';
    let mobileNumber: string = '';
    let memberId: string | undefined = undefined;
    let memberLcid: string | undefined = config.defaultLcid;

    if (isAuthenticated) {
      const memberDetails = await firstValueFrom(this.memberQuery.selectMemberDetails());
      emailAddress = memberDetails?.EmailAddress1 ?? '';
      mobileNumber = memberDetails?.MobileNumber ?? '';
      const user = this.memberQuery.getValue().user;
      memberLcid = user?.lcid;
      memberId = user?.memberId;
    }
    // Currency from the member's language, falling back to the app default language's currency.
    const currencyCode =
      this.languages.find((lang) => lang.lcid === memberLcid)?.currencyCode ??
      this.languages.find((lang) => lang.lcid === config.defaultLcid)?.currencyCode ??
      this.languages[0]?.currencyCode;

    entries.forEach(([providerId, options]) => {
      if (!options?.component) {
        this.logService.error(`ExpressCheckout: no component registered for provider '${ProviderName[providerId]}'`);
        return;
      }
      try {
        const componentRef = this.providerHost.createComponent(options.component);
        componentRef.setInput('context', {
          // 'registration' pays the wizard's basket via api/register/express/*; defaults to the
          // marketplace endpoints so existing hosts are unaffected.
          mode: this.context?.mode ?? 'marketplace',
          orderId: this.context?.orderId ?? '',
          amount: this.totalAmount,
          currencyCode,
          basketItems: this.context?.basketItems ?? [],
          // Prefer the dedicated input, falling back to the context so hosts that only set it there
          // keep working. Registration posts these on register/express/complete.
          giftCodes: this.giftCodes?.length ? this.giftCodes : (this.context?.giftCodes ?? []),
          emailAddress,
          mobileNumber,
          memberId,
          useCardToken: this.context?.useCardToken,
          successUrl: this.context?.successUrl,
          failureUrl: this.context?.failureUrl,
        });

        this.subscriptions.add(
          componentRef.instance.result.subscribe((res) => {
            if (res.success && this.context?.successUrl) {
              this.router.navigate([this.context.successUrl], { queryParams: { orderId: res.data } });
            } else if (!res.success && this.context?.failureUrl) {
              // A failed express payment must be visible to the customer, matching the native flow.
              this.router.navigate([this.context.failureUrl]);
            }
            this.result.emit(res);
          })
        );
        this.subscriptions.add(componentRef.instance.incomplete.subscribe((res) => this.incomplete.emit(res)));

        this.componentRefs.push(componentRef);
      } catch (error) {
        // One provider failing must not prevent the others from rendering.
        this.logService.error(`ExpressCheckout: failed to create provider '${ProviderName[providerId]}'`, error);
      }
    });

    await this.awaitProvidersReady();
  }

  /**
   * Hold the row hidden until every provider has settled, so the buttons appear together rather
   * than popping in one at a time as each SDK returns.
   *
   * A provider counts as settled when it is ready and no longer loading: Paze and FreedomPay set
   * `ready` when their SDK resolves but keep a separate `loading` flag through a further
   * eligibility check, and their templates gate on both. These live on the base component rather
   * than the ExpressCheckoutProvider interface, so they are read defensively here - re-running
   * init instead would initialise every SDK twice.
   */
  private async awaitProvidersReady(): Promise<void> {
    try {
      const deadline = Date.now() + ExpressCheckoutComponent.READY_TIMEOUT_MS;

      while (Date.now() < deadline) {
        const allSettled = this.componentRefs.every((componentRef) => {
          const provider = componentRef.instance as { ready?: boolean; loading?: boolean };
          return provider.ready && !provider.loading;
        });
        if (allSettled) {
          break;
        }

        // Flush each tick: a provider that sets `ready` after an await is outside any change
        // detection pass, and PayPal has no detectChanges of its own.
        this.cdr.detectChanges();
        await new Promise((resolve) => setTimeout(resolve, ExpressCheckoutComponent.READY_POLL_MS));
      }
    } finally {
      // In a finally so the row is revealed even if the wait throws: leaving it hidden would lose
      // express checkout entirely, which is worse than revealing the buttons unevenly.
      this.revealProviders();
    }
  }

  /** Drop the loading state and show whatever the providers rendered. */
  private revealProviders(): void {
    this.initialising = false;
    // Runs after an await, outside the pass that created these components, so the change has to be
    // flushed manually or the loader never clears.
    this.cdr.detectChanges();
  }

  /** Tear down all rendered provider components and their subscriptions. */
  private clearProviders(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.componentRefs.forEach((ref) => ref.destroy());
    this.componentRefs = [];
    this.providerHost?.clear();
  }
}
